/**
 * The handle that raises and lowers the chat over a full-screen map.
 *
 * On a phone in the navigation mode the conversation becomes a bottom sheet:
 * collapsed it shows the guide's last line, the chips and the composer, which
 * is everything somebody walking needs; expanded it is the ordinary thread.
 *
 * **The chat itself is not re-rendered here.** The sheet is `.app__chat` under
 * a different set of CSS rules, so `Thread`, `Chips` and `Composer` stay
 * exactly where they are and keep their scroll, their draft and their state.
 * A second copy of the conversation for one layout is how two conversations
 * start disagreeing.
 *
 * **A touch decides the whole gesture and then calls `preventDefault()`.** A
 * mobile browser dispatches a compatibility `click` after `touchend`, and with
 * an `onClick` also on the handle that click read the state the swipe had just
 * written and toggled it straight back: a swipe up flashed the sheet open and
 * closed it again. Preventing the default suppresses that click, which leaves
 * `onClick` to the mouse and the keyboard — the two inputs that have no touch
 * events at all. The decision itself is `sheetAfterTouch`, tested separately.
 */

import { useRef } from 'react';

import { useNav } from './navStore';
import { sheetAfterTouch } from './sheet';

export default function NavSheet() {
  const sheet = useNav((state) => state.sheet);
  const expanded = sheet === 'expanded';
  // A ref rather than a module-level variable: one sheet is on screen today,
  // and «today» is exactly the assumption a module-level mutable makes silently.
  const startY = useRef<number | null>(null);

  return (
    <button
      type="button"
      className="nav-sheet__handle"
      aria-expanded={expanded}
      aria-label={expanded ? 'Свернуть разговор' : 'Развернуть разговор'}
      // Mouse and keyboard only — a touch never reaches this, see the header.
      onClick={() => useNav.getState().setSheet(expanded ? 'collapsed' : 'expanded')}
      onTouchStart={(event) => {
        startY.current = event.touches[0]?.clientY ?? null;
      }}
      onTouchEnd={(event) => {
        const from = startY.current;
        startY.current = null;
        const to = event.changedTouches[0]?.clientY ?? null;
        const next = sheetAfterTouch(from, to, useNav.getState().sheet);
        // Suppresses the synthetic click. React attaches `touchend` as a
        // non-passive listener, so this is honoured (unlike `touchstart`).
        event.preventDefault();
        useNav.getState().setSheet(next);
      }}
    >
      <span className="nav-sheet__grip" aria-hidden="true" />
    </button>
  );
}
